// frontend/src/components/communityBasedAssessment/CommunityBasedAssessmentForm.jsx
import React, { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";
import Select from "react-select";

// Options for all the assessment dropdowns - matching the CRP-21 form exactly
const PHYSICAL_MOBILITY_OPTIONS = [
  { value: "Sit/stand in one area", label: "Sit/stand in one area" },
  { value: "Fair ambulation", label: "Fair ambulation" },
  { value: "Stairs/minor obstacles", label: "Stairs/minor obstacles" },
  { value: "Physical abilities", label: "Physical abilities" },
];

const ORIENTING_OPTIONS = [
  { value: "Small area only", label: "Small area only" },
  { value: "One room", label: "One room" },
  { value: "Several rooms", label: "Several rooms" },
  { value: "Building-wide", label: "Building-wide" },
  { value: "Building and grounds", label: "Building and grounds" },
];

const ENDURANCE_OPTIONS = [
  { value: "Works < 2 hours", label: "Works < 2 hours" },
  { value: "Works 2-3 hours", label: "Works 2-3 hours" },
  { value: "Works 3-4 hours", label: "Works 3-4 hours" },
  { value: "Works > 4 hours", label: "Works > 4 hours" },
];

const STRENGTH_LIFTING_OPTIONS = [
  { value: "Poor (<10 lbs.)", label: "Poor (<10 lbs.)" },
  { value: "Fair (10-20 lbs.)", label: "Fair (10-20 lbs.)" },
  { value: "Average (30-40 lbs.)", label: "Average (30-40 lbs.)" },
  { value: "Strong (>50 lbs.)", label: "Strong (>50 lbs.)" },
];

const WORK_RATE_OPTIONS = [
  { value: "Slow pace", label: "Slow pace" },
  { value: "Steady/average pace", label: "Steady/average pace" },
  { value: "Above average", label: "Above average" },
  { value: "Sometimes fast", label: "Sometimes fast" },
  { value: "Continual fast pace", label: "Continual fast pace" },
];

const APPEARANCE_OPTIONS = [
  { value: "Unkempt/poor hygiene", label: "Unkempt/poor hygiene" },
  { value: "Unkempt/clean", label: "Unkempt/clean" },
  { value: "Neat/clean but clothing unmatched", label: "Neat/clean but clothing unmatched" },
  { value: "Neat/clean and clothing matched", label: "Neat/clean and clothing matched" },
];

const COMMUNICATION_OPTIONS = [
  { value: "Uses sounds/gestures", label: "Uses sounds/gestures" },
  { value: "Uses keywords/signs", label: "Uses keywords/signs" },
  { value: "Speaks unclearly", label: "Speaks unclearly" },
  { value: "Communicates clearly, intelligible to strangers", label: "Communicates clearly, intelligible to strangers" },
];

const SOCIAL_INTERACTIONS_OPTIONS = [
  { value: "Rarely interacts", label: "Rarely interacts" },
  { value: "Appropriately", label: "Appropriately" },
  { value: "Polite, responses appropriate", label: "Polite, responses appropriate" },
  { value: "Initiates social interactions", label: "Initiates social interactions" },
  { value: "Initiates social interactions infrequently", label: "Initiates social interactions infrequently" },
];

const ATTENTION_PERSEVERANCE_OPTIONS = [
  { value: "Frequent prompts required", label: "Frequent prompts required" },
  { value: "Intermittent prompts/high supervision", label: "Intermittent prompts/high supervision" },
  { value: "Intermittent prompts/low supervision", label: "Intermittent prompts/low supervision" },
  { value: "Infrequent prompts/low supervision", label: "Infrequent prompts/low supervision" },
  { value: "Infrequent supervision", label: "Infrequent supervision" },
];

const JOB_SEQUENCING_OPTIONS = [
  { value: "Cannot perform tasks in sequence", label: "Cannot perform tasks in sequence" },
  { value: "Performs 2-3 tasks in sequence", label: "Performs 2-3 tasks in sequence" },
  { value: "Performs 4-6 tasks in sequence", label: "Performs 4-6 tasks in sequence" },
  { value: "Performs 7 or more tasks in sequence", label: "Performs 7 or more tasks in sequence" },
];

const INITIATIVE_MOTIVATION_OPTIONS = [
  { value: "Always seeks work", label: "Always seeks work" },
  { value: "Sometimes volunteers", label: "Sometimes volunteers" },
  { value: "Waits for directions", label: "Waits for directions" },
  { value: "Avoids next task", label: "Avoids next task" },
];

const ADAPTING_CHANGE_OPTIONS = [
  { value: "Adapts to change", label: "Adapts to change" },
  { value: "Adapts to change with some difficulty", label: "Adapts to change with some difficulty" },
  { value: "Adapts to change with great difficulty", label: "Adapts to change with great difficulty" },
  { value: "Rigid routine", label: "Rigid routine" },
];

const REINFORCEMENT_NEEDS_OPTIONS = [
  { value: "Frequently required", label: "Frequently required" },
  { value: "Daily", label: "Daily" },
  { value: "Weekly", label: "Weekly" },
  { value: "Paycheck sufficient", label: "Paycheck sufficient" },
];

const LEVEL_SUPPORT_OPTIONS = [
  { value: "Very supportive of work", label: "Very supportive of work" },
  { value: "Supportive of work with reservation", label: "Supportive of work with reservation" },
  { value: "Indifferent about work", label: "Indifferent about work" },
  { value: "Negative about work", label: "Negative about work" },
];

const DISCRIMINATION_SKILLS_OPTIONS = [
  { value: "Cannot distinguish between work supplies", label: "Cannot distinguish between work supplies" },
  { value: "Distinguishes between work supplies", label: "Distinguishes between work supplies" },
  { value: "Distinguishes with an external cue", label: "Distinguishes with an external cue" },
];

const TIME_AWARENESS_OPTIONS = [
  { value: "Unaware of time and clock function", label: "Unaware of time and clock function" },
  { value: "Identifies breaks/lunch", label: "Identifies breaks/lunch" },
  { value: "Can tell time to the hour", label: "Can tell time to the hour" },
  { value: "Can tell time in hours/minutes", label: "Can tell time in hours/minutes" },
];

const FUNCTIONAL_READING_OPTIONS = [
  { value: "None", label: "None" },
  { value: "Sight words/symbols", label: "Sight words/symbols" },
  { value: "Simple reading", label: "Simple reading" },
  { value: "Fluent reading", label: "Fluent reading" },
];

const FUNCTIONAL_MATH_OPTIONS = [
  { value: "None", label: "None" },
  { value: "Simple counting", label: "Simple counting" },
  { value: "Simple addition/subtraction", label: "Simple addition/subtraction" },
  { value: "Computational skills", label: "Computational skills" },
];

const STREET_CROSSING_OPTIONS = [
  { value: "None", label: "None" },
  { value: "2-lane street (with or w/o light)", label: "2-lane street (with or w/o light)" },
  { value: "4-lane street (with or w/o light)", label: "4-lane street (with or w/o light)" },
];

const HANDLING_CRITICISM_OPTIONS = [
  { value: "Resistive/argumentative", label: "Resistive/argumentative" },
  { value: "Withdraws into silence", label: "Withdraws into silence" },
  { value: "Accepts criticism/does not change", label: "Accepts criticism/does not change" },
];

const DISRUPTIVE_BEHAVIOR_OPTIONS = [
  { value: "Yelling, screaming", label: "Yelling, screaming" },
  { value: "Clinging", label: "Clinging" },
  { value: "Laughing/crying for no reason", label: "Laughing/crying for no reason" },
  { value: "Interrupting", label: "Interrupting" },
  { value: "None observed", label: "None observed" },
];

const REPETITIVE_BEHAVIOR_OPTIONS = [
  { value: "Pacing", label: "Pacing" },
  { value: "Rocking", label: "Rocking" },
  { value: "Twirling fingers", label: "Twirling fingers" },
  { value: "Twitching", label: "Twitching" },
  { value: "None observed", label: "None observed" },
];

const OFFENSIVE_BEHAVIOR_OPTIONS = [
  { value: "Talking too loudly", label: "Talking too loudly" },
  { value: "Burping, picking nose", label: "Burping, picking nose" },
  { value: "Touching, hugging", label: "Touching, hugging" },
  { value: "None observed", label: "None observed" },
];

const WITHDRAWAL_BEHAVIOR_OPTIONS = [
  { value: "Keeping away from people", label: "Keeping away from people" },
  { value: "Expresses unusual fears", label: "Expresses unusual fears" },
  { value: "Shows little interest in activities", label: "Shows little interest in activities" },
  { value: "None observed", label: "None observed" },
];

const NONCOMPLIANT_BEHAVIOR_OPTIONS = [
  { value: "Refusing to attend school/work", label: "Refusing to attend school/work" },
  { value: "Refusing to follow rules/requests", label: "Refusing to follow rules/requests" },
  { value: "Acting defiant/pouting", label: "Acting defiant/pouting" },
  { value: "None observed", label: "None observed" },
];

const MONEY_SKILLS_OPTIONS = [
  { value: "Discriminates between coins", label: "Discriminates between coins" },
  { value: "Makes minor purchases", label: "Makes minor purchases" },
  { value: "Makes major purchases", label: "Makes major purchases" },
  { value: "Understands amount of spending", label: "Understands amount of spending" },
  { value: "Money given to customer", label: "Money given to customer" },
  { value: "Family willing to give customer money from paycheck", label: "Family willing to give customer money from paycheck" },
];

const ASKING_ASSISTANCE_OPTIONS = [
  { value: "Peers", label: "Peers" },
  { value: "Co-workers", label: "Co-workers" },
  { value: "Acquaintances", label: "Acquaintances" },
  { value: "Persons in authority", label: "Persons in authority" },
];

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "completed", label: "Completed" },
  { value: "archived", label: "Archived" },
];

const initialFormState = {
  consumer_id: "",
  authorization_id: "",
  person_completing_form: "",
  assessment_site_id: "",
  assessment_site_text: "",
  dates_hours: "",
  total_hours: "",
  // Page 1
  physical_mobility: "", physical_mobility_summary: "",
  orienting: "", orienting_summary: "",
  endurance: "", endurance_summary: "",
  strength_lifting: "", strength_lifting_summary: "",
  // Page 2
  work_rate: "", work_rate_summary: "",
  appearance: "", appearance_summary: "",
  communication: "", communication_summary: "",
  social_interactions: "", social_interactions_summary: "",
  attention_perseverance: "", attention_perseverance_summary: "",
  // Page 3
  job_sequencing: "", job_sequencing_summary: "",
  initiative_motivation: "", initiative_motivation_summary: "",
  adapting_change: "", adapting_change_summary: "",
  reinforcement_needs: "", reinforcement_needs_summary: "",
  level_support: "", level_support_summary: "",
  // Page 4
  discrimination_skills: "", discrimination_skills_summary: "",
  time_awareness: "", time_awareness_summary: "",
  functional_reading: "", functional_reading_summary: "",
  functional_math: "", functional_math_summary: "",
  street_crossing: "", street_crossing_summary: "",
  handling_criticism: "", handling_criticism_summary: "",
  // Page 5
  disruptive_behavior: "", disruptive_behavior_summary: "",
  repetitive_behavior: "", repetitive_behavior_summary: "",
  offensive_behavior: "", offensive_behavior_summary: "",
  withdrawal_behavior: "", withdrawal_behavior_summary: "",
  noncompliant_behavior: "", noncompliant_behavior_summary: "",
  // Page 6
  money_skills: "", money_skills_summary: "",
  asking_assistance: "", asking_assistance_summary: "",
  other_observations: "", other_observations_summary: "",
  status: "draft",
};

const CommunityBasedAssessmentForm = ({ assessment, onCancel, onSave, onGeneratePDF }) => {
  const { user, tenant_id: tenantId } = useContext(AuthContext);
  const apiBase = getApiBaseURL();
  const userId = user?.user_id;
  const userRole = user?.role;
  const isAdmin = user?.is_admin;

  const [formData, setFormData] = useState(initialFormState);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Dropdown options
  const [consumers, setConsumers] = useState([]);
  const [jobCoaches, setJobCoaches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [authorizations, setAuthorizations] = useState([]);
  const [useCustomSite, setUseCustomSite] = useState(false);

  // Current page/section for multi-page form
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = 6;
  
  // Ref to scroll to top of form
  const formTopRef = useRef(null);

  // Scroll to top when page changes
  useEffect(() => {
    if (formTopRef.current) {
      formTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // Force layout recalculation
    window.dispatchEvent(new Event('resize'));
  }, [currentPage]);

  // Load dropdown data
  useEffect(() => {
    if (!tenantId) return;

    secureAxios
      .get(`${apiBase}/api/community-based-assessments/consumers`, {
        params: { tenant_id: tenantId, user_id: userId, role: userRole },
      })
      .then((res) => setConsumers(res.data))
      .catch((err) => console.error("Error loading consumers:", err));

    console.log("Loading locations with params:", { tenant_id: tenantId, user_id: userId, is_admin: isAdmin });
    
    secureAxios
      .get(`${apiBase}/api/community-based-assessments/locations`, {
        params: { tenant_id: tenantId, user_id: userId, is_admin: isAdmin === true ? 'true' : 'false' },
      })
      .then((res) => {
        console.log("Locations API response:", res.data);
        setLocations(res.data || []);
      })
      .catch((err) => console.error("Error loading locations:", err));
  }, [tenantId, userId, userRole, isAdmin, apiBase]);

  // Load job coaches when consumer changes
  useEffect(() => {
    if (!tenantId) return;

    secureAxios
      .get(`${apiBase}/api/community-based-assessments/job-coaches`, {
        params: { tenant_id: tenantId, consumer_id: formData.consumer_id },
      })
      .then((res) => {
        setJobCoaches(res.data);
        if (!assessment && res.data.length > 0) {
          const assigned = res.data.find((jc) => jc.is_assigned);
          if (assigned) {
            setFormData((prev) => ({
              ...prev,
              person_completing_form: assigned.user_id,
            }));
          }
        }
      })
      .catch((err) => console.error("Error loading job coaches:", err));
  }, [tenantId, formData.consumer_id, assessment, apiBase]);

  // Load authorizations when consumer changes
  useEffect(() => {
    if (!tenantId || !formData.consumer_id) {
      setAuthorizations([]);
      return;
    }

    secureAxios
      .get(
        `${apiBase}/api/community-based-assessments/authorizations/${formData.consumer_id}`,
        { params: { tenant_id: tenantId } }
      )
      .then((res) => setAuthorizations(res.data))
      .catch((err) => console.error("Error loading authorizations:", err));
  }, [tenantId, formData.consumer_id, apiBase]);

  // Populate form when editing
  useEffect(() => {
    if (assessment) {
      const newFormData = { ...initialFormState };
      Object.keys(initialFormState).forEach(key => {
        if (assessment[key] !== undefined && assessment[key] !== null) {
          newFormData[key] = assessment[key];
        }
      });
      setFormData(newFormData);

      if (assessment.assessment_site_text && !assessment.assessment_site_id) {
        setUseCustomSite(true);
      }
    } else {
      setFormData({
        ...initialFormState,
        person_completing_form: userId,
      });
    }
  }, [assessment, userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, option) => {
    setFormData((prev) => ({
      ...prev,
      [name]: option ? option.value : "",
    }));
  };

  const handleSubmit = async (e, forceSubmit = false) => {
    e.preventDefault();
    console.log("handleSubmit triggered! Current page:", currentPage, "Loading:", loading, "Force:", forceSubmit);

    // Prevent double submission
    if (loading) {
      console.log("Blocked submission - already loading");
      return;
    }

    // Only allow submission from the last page for new assessments (unless forced for edit mode)
    if (!forceSubmit && currentPage !== totalPages) {
      console.log("Blocked submission - not on last page");
      return;
    }

    if (!formData.consumer_id) {
      toast.error("Please select a consumer");
      setCurrentPage(1);
      return;
    }

    setLoading(true);

    const payload = {
      ...formData,
      tenant_id: tenantId,
      created_by: userId,
      updated_by: userId,
      assessment_site_id: useCustomSite ? null : formData.assessment_site_id,
      assessment_site_text: useCustomSite ? formData.assessment_site_text : null,
    };

    try {
      if (assessment) {
        await secureAxios.put(
          `${apiBase}/api/community-based-assessments/${assessment.assessment_id}`,
          payload
        );
        toast.success("Assessment updated successfully");
      } else {
        await secureAxios.post(
          `${apiBase}/api/community-based-assessments`,
          payload
        );
        toast.success("Assessment created successfully");
      }
      onSave();
    } catch (err) {
      console.error("Error saving assessment:", err);
      toast.error(err.response?.data?.error || "Failed to save assessment");
    } finally {
      setLoading(false);
    }
  };

  const consumerOptions = consumers.map((c) => ({
    value: c.consumer_id,
    label: `${c.name}${c.case_id ? ` (${c.case_id})` : ""}`,
  }));

  const jobCoachOptions = jobCoaches.map((jc) => ({
    value: jc.user_id,
    label: `${jc.name}${jc.is_assigned ? " (Assigned)" : ""}`,
  }));

  const locationOptions = locations.map((l) => ({
    value: l.location_id,
    label: l.location_name,
  }));

  const authorizationOptions = authorizations.map((a) => ({
    value: a.authorization_id,
    label: `${a.authorization_number || "No #"} - ${a.service_name} (${a.authorization_hours || 0} hrs)`,
  }));

  // Helper to render an assessment field with its summary (side by side)
  const renderAssessmentFieldWithSummary = (label, fieldName, options) => (
    <div className="card mb-3">
      <div className="card-header bg-light py-2">
        <strong>{label}</strong>
      </div>
      <div className="card-body">
        <div className="row">
          <div className="col-md-6">
            <label className="form-label small text-muted">Functional Assessment Data</label>
            <Select
              options={options}
              value={options.find((o) => o.value === formData[fieldName]) || null}
              onChange={(opt) => handleSelectChange(fieldName, opt)}
              placeholder="Select..."
              isClearable
              classNamePrefix="react-select"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label small text-muted">Assessment Summary</label>
            <textarea
              className="form-control"
              name={`${fieldName}_summary`}
              rows="3"
              value={formData[`${fieldName}_summary`] || ""}
              onChange={handleChange}
              placeholder="Describe behavior, characteristics, frequency, environment..."
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderPageContent = () => {
    switch (currentPage) {
      case 1:
        return (
          <>
            <h5 className="border-bottom pb-2 mb-4">
              <i className="bi bi-person me-2"></i>
              Header Information
            </h5>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Consumer <span className="text-danger">*</span>
                </label>
                <Select
                  options={consumerOptions}
                  value={consumerOptions.find((o) => o.value === formData.consumer_id) || null}
                  onChange={(opt) => handleSelectChange("consumer_id", opt)}
                  placeholder="Select Consumer..."
                  isClearable
                  classNamePrefix="react-select"
                  isDisabled={!!assessment}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Person Completing Form</label>
                <Select
                  options={jobCoachOptions}
                  value={jobCoachOptions.find((o) => o.value === formData.person_completing_form) || null}
                  onChange={(opt) => handleSelectChange("person_completing_form", opt)}
                  placeholder="Select Job Coach..."
                  isClearable
                  classNamePrefix="react-select"
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Assessment Site</label>
                <div className="d-flex align-items-center mb-2">
                  <div className="form-check me-3">
                    <input
                      type="radio"
                      className="form-check-input"
                      id="useLocationDropdown"
                      checked={!useCustomSite}
                      onChange={() => setUseCustomSite(false)}
                    />
                    <label className="form-check-label" htmlFor="useLocationDropdown">
                      Select Location
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      type="radio"
                      className="form-check-input"
                      id="useCustomSite"
                      checked={useCustomSite}
                      onChange={() => setUseCustomSite(true)}
                    />
                    <label className="form-check-label" htmlFor="useCustomSite">
                      Enter Custom Site
                    </label>
                  </div>
                </div>
                {useCustomSite ? (
                  <input
                    type="text"
                    className="form-control"
                    name="assessment_site_text"
                    value={formData.assessment_site_text}
                    onChange={handleChange}
                    placeholder="Enter assessment site name..."
                  />
                ) : (
                  <Select
                    options={locationOptions}
                    value={locationOptions.find((o) => o.value === formData.assessment_site_id) || null}
                    onChange={(opt) => handleSelectChange("assessment_site_id", opt)}
                    placeholder="Select Location..."
                    isClearable
                    classNamePrefix="react-select"
                  />
                )}
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Dates/Hours</label>
                <input
                  type="text"
                  className="form-control"
                  name="dates_hours"
                  value={formData.dates_hours}
                  onChange={handleChange}
                  placeholder="e.g., 01/15 - 01/20"
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Total Hours</label>
                <input
                  type="text"
                  className="form-control"
                  name="total_hours"
                  value={formData.total_hours}
                  onChange={handleChange}
                  placeholder="e.g., 20"
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Tie to Authorization <small className="text-muted">(internal use only)</small>
                </label>
                <Select
                  options={authorizationOptions}
                  value={authorizationOptions.find((o) => o.value === formData.authorization_id) || null}
                  onChange={(opt) => handleSelectChange("authorization_id", opt)}
                  placeholder="Select Authorization (optional)..."
                  isClearable
                  classNamePrefix="react-select"
                  isDisabled={!formData.consumer_id}
                />
                <small className="text-muted">
                  Links to authorization for tracking. Not displayed on PDF.
                </small>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Status</label>
                <Select
                  options={STATUS_OPTIONS}
                  value={STATUS_OPTIONS.find((o) => o.value === formData.status) || STATUS_OPTIONS[0]}
                  onChange={(opt) => handleSelectChange("status", opt)}
                  classNamePrefix="react-select"
                />
              </div>
            </div>

            <hr className="my-4" />

            <h5 className="border-bottom pb-2 mb-4">
              <i className="bi bi-1-circle me-2"></i>
              Page 1: Physical Assessment
            </h5>

            {renderAssessmentFieldWithSummary("Strength, Lifting and Carrying", "strength_lifting", STRENGTH_LIFTING_OPTIONS)}
            {renderAssessmentFieldWithSummary("Endurance", "endurance", ENDURANCE_OPTIONS)}
            {renderAssessmentFieldWithSummary("Orienting", "orienting", ORIENTING_OPTIONS)}
            {renderAssessmentFieldWithSummary("Physical Mobility", "physical_mobility", PHYSICAL_MOBILITY_OPTIONS)}
          </>
        );

      case 2:
        return (
          <>
            <h5 className="border-bottom pb-2 mb-4">
              <i className="bi bi-2-circle me-2"></i>
              Page 2: Work Behaviors
            </h5>

            {renderAssessmentFieldWithSummary("Independent Work Rate (no prompts)", "work_rate", WORK_RATE_OPTIONS)}
            {renderAssessmentFieldWithSummary("Appearance", "appearance", APPEARANCE_OPTIONS)}
            {renderAssessmentFieldWithSummary("Communication", "communication", COMMUNICATION_OPTIONS)}
            {renderAssessmentFieldWithSummary("Social Interactions", "social_interactions", SOCIAL_INTERACTIONS_OPTIONS)}
            {renderAssessmentFieldWithSummary("Attention to Task/Perseverance", "attention_perseverance", ATTENTION_PERSEVERANCE_OPTIONS)}
          </>
        );

      case 3:
        return (
          <>
            <h5 className="border-bottom pb-2 mb-4">
              <i className="bi bi-3-circle me-2"></i>
              Page 3: Job Performance
            </h5>

            {renderAssessmentFieldWithSummary("Independent sequencing of job duties", "job_sequencing", JOB_SEQUENCING_OPTIONS)}
            {renderAssessmentFieldWithSummary("Initiative/Motivation", "initiative_motivation", INITIATIVE_MOTIVATION_OPTIONS)}
            {renderAssessmentFieldWithSummary("Adapting to Change", "adapting_change", ADAPTING_CHANGE_OPTIONS)}
            {renderAssessmentFieldWithSummary("Reinforcement Needs", "reinforcement_needs", REINFORCEMENT_NEEDS_OPTIONS)}
            {renderAssessmentFieldWithSummary("Level Of Support", "level_support", LEVEL_SUPPORT_OPTIONS)}
          </>
        );

      case 4:
        return (
          <>
            <h5 className="border-bottom pb-2 mb-4">
              <i className="bi bi-4-circle me-2"></i>
              Page 4: Cognitive Skills
            </h5>

            {renderAssessmentFieldWithSummary("Discrimination Skills", "discrimination_skills", DISCRIMINATION_SKILLS_OPTIONS)}
            {renderAssessmentFieldWithSummary("Time Awareness", "time_awareness", TIME_AWARENESS_OPTIONS)}
            {renderAssessmentFieldWithSummary("Functional Reading", "functional_reading", FUNCTIONAL_READING_OPTIONS)}
            {renderAssessmentFieldWithSummary("Functional Math", "functional_math", FUNCTIONAL_MATH_OPTIONS)}
            {renderAssessmentFieldWithSummary("Independent Street Crossing", "street_crossing", STREET_CROSSING_OPTIONS)}
            {renderAssessmentFieldWithSummary("Handling Criticism/Stress", "handling_criticism", HANDLING_CRITICISM_OPTIONS)}
          </>
        );

      case 5:
        return (
          <>
            <h5 className="border-bottom pb-2 mb-4">
              <i className="bi bi-5-circle me-2"></i>
              Page 5: Behavioral Concerns
            </h5>

            {renderAssessmentFieldWithSummary("Disruptive Behavior that Interferes with Activities of Others", "disruptive_behavior", DISRUPTIVE_BEHAVIOR_OPTIONS)}
            {renderAssessmentFieldWithSummary("Usual or Repetitive Behavior/habits", "repetitive_behavior", REPETITIVE_BEHAVIOR_OPTIONS)}
            {renderAssessmentFieldWithSummary("Behavior that is Socially Offensive to Others", "offensive_behavior", OFFENSIVE_BEHAVIOR_OPTIONS)}
            {renderAssessmentFieldWithSummary("Withdrawal or Inattentive Behavior", "withdrawal_behavior", WITHDRAWAL_BEHAVIOR_OPTIONS)}
            {renderAssessmentFieldWithSummary("Uncooperative or Noncompliant Behavior", "noncompliant_behavior", NONCOMPLIANT_BEHAVIOR_OPTIONS)}
          </>
        );

      case 6:
        return (
          <>
            <h5 className="border-bottom pb-2 mb-4">
              <i className="bi bi-6-circle me-2"></i>
              Page 6: Additional Skills & Recommendations
            </h5>

            {renderAssessmentFieldWithSummary("Money Skills", "money_skills", MONEY_SKILLS_OPTIONS)}
            {renderAssessmentFieldWithSummary("Asking for Assistance", "asking_assistance", ASKING_ASSISTANCE_OPTIONS)}

            <div className="card mb-3">
              <div className="card-header bg-light py-2">
                <strong>Other Observations and/or Recommendations</strong>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Data</label>
                    <textarea
                      className="form-control"
                      name="other_observations"
                      rows="5"
                      value={formData.other_observations}
                      onChange={handleChange}
                      placeholder="Enter any other observations or recommendations..."
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Assessment Summary</label>
                    <textarea
                      className="form-control"
                      name="other_observations_summary"
                      rows="5"
                      value={formData.other_observations_summary}
                      onChange={handleChange}
                      placeholder="Enter assessment summary..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="container my-4">
    <div className="community-based-assessment-form" ref={formTopRef} style={{ minWidth: 0, overflow: 'visible' }}>
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h4 className="mb-1">
            <i className="bi bi-clipboard-data me-2"></i>
            {assessment ? "Edit" : "Create"} CRP-21: Community Based Assessment
          </h4>
          <p className="text-muted mb-0">
            Complete the functional assessment worksheet for the consumer
          </p>
        </div>
        <button type="button" className="btn btn-outline-secondary" onClick={onCancel}>
          <i className="bi bi-x-circle me-1"></i>
          Cancel
        </button>
      </div>

      <div className="card mb-4">
        <div className="card-body py-3">
          {/* Desktop layout */}
          <div className="d-none d-md-flex justify-content-between align-items-center">
            <div className="d-flex gap-2 flex-wrap">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  type="button"
                  className={`btn btn-sm ${currentPage === page ? "btn-primary" : "btn-outline-secondary"}`}
                  onClick={() => setCurrentPage(page)}
                >
                  Page {page}
                </button>
              ))}
            </div>
            <div className="d-flex flex-column align-items-end">
              <div className="d-flex align-items-center gap-3">
                {assessment ? (
                  <div className="d-flex gap-1">
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={(e) => {
                        e.preventDefault();
                        handleSubmit(e, true);
                      }}
                      disabled={loading}
                      title="Save Assessment"
                    >
                      {loading ? (
                        <span className="spinner-border spinner-border-sm"></span>
                      ) : (
                        <i className="bi bi-floppy"></i>
                      )}
                    </button>
                    {onGeneratePDF && (
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={onGeneratePDF}
                        disabled={loading || pdfLoading}
                        title="Generate PDF"
                      >
                        {pdfLoading ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          <i className="bi bi-file-earmark-pdf"></i>
                        )}
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={onCancel}
                      disabled={loading}
                      title="Close"
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  </div>
                ) : (
                  <small className="text-muted fst-italic">
                    <i className="bi bi-info-circle me-1"></i>
                    Save button on Page 6
                  </small>
                )}
                <small className="text-muted">
                  Page {currentPage} of {totalPages}
                </small>
              </div>
              {assessment && (
                <small className="text-muted fst-italic mt-1 text-center w-100" style={{ fontSize: '0.7rem' }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Document library updated at PDF generation
                </small>
              )}
            </div>
          </div>

          {/* Mobile layout - stacked rows */}
          <div className="d-md-none d-flex flex-column align-items-center gap-2">
            {/* Row 1: Page buttons */}
            <div className="d-flex gap-1 flex-wrap justify-content-center">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  type="button"
                  className={`btn btn-sm ${currentPage === page ? "btn-primary" : "btn-outline-secondary"}`}
                  onClick={() => setCurrentPage(page)}
                >
                  Pg {page}
                </button>
              ))}
            </div>
            
            {/* Row 2: Action icons */}
            {assessment ? (
              <div className="d-flex gap-1">
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSubmit(e, true);
                  }}
                  disabled={loading}
                  title="Save Assessment"
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm"></span>
                  ) : (
                    <i className="bi bi-floppy"></i>
                  )}
                </button>
                {onGeneratePDF && (
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={onGeneratePDF}
                    disabled={loading || pdfLoading}
                    title="Generate PDF"
                  >
                    {pdfLoading ? (
                      <span className="spinner-border spinner-border-sm"></span>
                    ) : (
                      <i className="bi bi-file-earmark-pdf"></i>
                    )}
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={onCancel}
                  disabled={loading}
                  title="Close"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            ) : (
              <small className="text-muted fst-italic">
                <i className="bi bi-info-circle me-1"></i>
                Save button on Page 6
              </small>
            )}
            
            {/* Row 3: Document library note (only in edit mode) */}
            {assessment && (
              <small className="text-muted fst-italic text-center" style={{ fontSize: '0.7rem' }}>
                <i className="bi bi-info-circle me-1"></i>
                Document library updated at PDF generation
              </small>
            )}
            
            {/* Row 4: Page info */}
            <small className="text-muted">
              Page {currentPage} of {totalPages}
            </small>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card">
          <div className="card-body">{renderPageContent()}</div>
        </div>

        <div className="d-flex justify-content-between mt-4">
          <div>
            {currentPage > 1 && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                <i className="bi bi-arrow-left me-1"></i>
                Previous
              </button>
            )}
          </div>

          <div className="d-flex gap-2">
            {currentPage < totalPages ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setCurrentPage((p) => p + 1);
                }}
              >
                Next
                <i className="bi bi-arrow-right ms-1"></i>
              </button>
            ) : (
              <button type="submit" className="btn btn-success" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-1"></i>
                    {assessment ? "Update Assessment" : "Create Assessment"}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
    </div>
  );
};

export default CommunityBasedAssessmentForm;